import type { MatchSettings } from '@vm/shared';
import type { JoinInfo } from '@vm/sim';

export interface Transport {
  send(data: Uint8Array): void;
  close(): void;
  onOpen: () => void;
  onMessage: (data: Uint8Array) => void;
  onClose: (reason: string) => void;
  /** Bytes received (stats). */
  bytesIn: number;
  bytesOut: number;
}

/**
 * Dev-only network simulation (`?lag=<ms one-way>&jitter=<ms>`): delays messages in both
 * directions while preserving order, like a TCP stream.
 */
function lagWrap(t: Transport): Transport {
  const q = new URLSearchParams(location.search);
  const lag = Number(q.get('lag') ?? 0);
  const jitter = Number(q.get('jitter') ?? 0);
  if (!import.meta.env.DEV || (lag <= 0 && jitter <= 0)) return t;
  let lastIn = 0;
  let lastOut = 0;
  const delay = (last: number): number => Math.max(last, performance.now() + lag + Math.random() * jitter);
  const rawSend = t.send;
  t.send = (d) => {
    const copy = d.slice();
    lastOut = delay(lastOut);
    setTimeout(() => rawSend(copy), lastOut - performance.now());
  };
  const deliver = t.onMessage;
  let handler = deliver;
  Object.defineProperty(t, 'onMessage', {
    get: () => (d: Uint8Array) => {
      lastIn = delay(lastIn);
      setTimeout(() => handler(d), lastIn - performance.now());
    },
    set: (fn: (d: Uint8Array) => void) => {
      handler = fn;
    },
  });
  return t;
}

export function wsTransport(url: string): Transport {
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';
  const t: Transport = {
    bytesIn: 0,
    bytesOut: 0,
    send: (d) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(d as Uint8Array<ArrayBuffer>);
        t.bytesOut += d.byteLength;
      }
    },
    close: () => ws.close(1000, 'bye'),
    onOpen: () => {},
    onMessage: () => {},
    onClose: () => {},
  };
  let kickReason = '';
  ws.onopen = () => t.onOpen();
  ws.onmessage = (e) => {
    const d = new Uint8Array(e.data as ArrayBuffer);
    t.bytesIn += d.byteLength;
    // Remember kick reasons (S2C.Kick = 9) so the close handler can surface them.
    if (d[0] === 9) kickReason = new TextDecoder().decode(d.subarray(2));
    t.onMessage(d);
  };
  ws.onclose = (e) => t.onClose(kickReason || e.reason || (e.code === 1006 ? 'Connection lost' : 'Disconnected'));
  ws.onerror = () => {};
  return lagWrap(t);
}

export interface OfflineStart {
  type: 'start';
  settings: MatchSettings;
  info: JoinInfo;
}

/** Runs the authoritative simulation in a worker for offline practice and tutorials. */
export function offlineTransport(settings: MatchSettings, info: JoinInfo): Transport {
  const worker = new Worker(new URL('../../offline/sim.worker.ts', import.meta.url), { type: 'module' });
  const t: Transport = {
    bytesIn: 0,
    bytesOut: 0,
    send: (d) => {
      const copy = d.slice();
      worker.postMessage(copy, [copy.buffer]);
      t.bytesOut += d.byteLength;
    },
    close: () => {
      worker.postMessage({ type: 'stop' });
      setTimeout(() => worker.terminate(), 100);
    },
    onOpen: () => {},
    onMessage: () => {},
    onClose: () => {},
  };
  worker.onmessage = (e: MessageEvent) => {
    const d = e.data;
    if (d instanceof Uint8Array) {
      t.bytesIn += d.byteLength;
      t.onMessage(d);
    } else if (d?.type === 'ready') t.onOpen();
    else if (d?.type === 'closed') t.onClose(d.reason ?? 'Match ended');
    else if (d?.type === 'error') t.onClose(`Simulation error: ${d.message}`);
  };
  worker.onerror = (e) => t.onClose(`Simulation crashed: ${e.message}`);
  const start: OfflineStart = { type: 'start', settings, info };
  worker.postMessage(start);
  return t;
}
